import React, { useState } from "react";
import { motion } from "framer-motion";
import { calculateGuiltScore } from "../utils/enhancedGuiltAlgorithm"; // Adjust path if needed

const moods = [
  { value: "bored", label: "Bored", emoji: "😑" },
  { value: "sad", label: "Sad", emoji: "😢" },
  { value: "happy", label: "Happy", emoji: "😊" },
  { value: "drunk", label: "Drunk", emoji: "🍸" },
  { value: "celebrating", label: "Celebrating", emoji: "🎉" },
  { value: "cute", label: "Just Felt Cute", emoji: "💅" },
  { value: "fomo", label: "FOMO", emoji: "👀" },
];

const commonItems = [
  "Shoes", "Dress", "Jacket", "Candle", "Plant", "Book", 
  "Skincare", "Makeup", "Takeout", "Coffee", "Gadget", 
  "Subscription", "Home decor", "Concert tickets", "Jewelry"
];

const PurchaseInput = ({ savePurchase, theme, userProfile }) => {
  const [formData, setFormData] = useState({
    item: "",
    amount: "",
    purchaseDate: new Date().toISOString().split("T")[0],
    mood: "happy",
    image: null,
    usageFrequency: "occasionally",
    stillHappy: "kinda",
    buyAgain: "maybe",
  });

  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });

    if (name === "item" && value.length > 1) {
      const filtered = commonItems.filter(item => 
        item.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const handleMoodSelect = (mood) => {
    setFormData({ ...formData, mood });
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const guiltScore = calculateGuiltScore(formData, userProfile || {});

    const purchase = {
      ...formData,
      id: Date.now(),
      date: new Date().toISOString(),
      guiltScore,
    };

    savePurchase(purchase);
  };

  const selectSuggestion = (suggestion) => {
    setFormData({ ...formData, item: suggestion });
    setShowSuggestions(false);
  };

  const buttonStyles = {
    mint: "bg-emerald-500 hover:bg-emerald-600",
    pink: "bg-pink-500 hover:bg-pink-600",
    lavender: "bg-purple-500 hover:bg-purple-600"
  };

  const accentStyles = {
    mint: "border-emerald-500 text-emerald-600",
    pink: "border-pink-500 text-pink-600",
    lavender: "border-purple-500 text-purple-600"
  };

  return (
    <motion.div 
      className="bg-white rounded-3xl shadow-xl p-6 mb-8"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.3 }}
    >
      <h2 className="text-2xl font-bold mb-6 text-center">What did you buy?</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type="text"
            name="item"
            value={formData.item}
            onChange={handleChange}
            placeholder="What did you buy?"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-blue-500"
            required
          />
          {showSuggestions && (
            <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-b-xl mt-1 shadow-lg">
              {filteredSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  className="px-4 py-2 cursor-pointer hover:bg-gray-100"
                  onClick={() => selectSuggestion(suggestion)}
                >
                  {suggestion}
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <input
            type="number"
            name="amount"
            value={formData.amount}
            onChange={handleChange}
            placeholder="How much was it? (e.g. 29.99)"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-blue-500"
            min="0"
            step="0.01"
            required
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">When did you buy it?</label>
          <input
            type="date"
            name="purchaseDate"
            value={formData.purchaseDate}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-2">How were you feeling when you bought it?</label>
          <div className="flex flex-wrap gap-2 justify-center">
            {moods.map((mood) => (
              <motion.button
                type="button"
                key={mood.value}
                onClick={() => handleMoodSelect(mood.value)}
                className={`px-3 py-2 rounded-full border ${
                  formData.mood === mood.value
                    ? `${accentStyles[theme]} border-2`
                    : "border-gray-300"
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <span className="text-xl mr-1">{mood.emoji}</span>
                <span className="text-sm">{mood.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Add a photo (optional)</label>
          <div className="flex items-center space-x-2">
            <input
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className={`px-4 py-2 rounded-xl border border-dashed cursor-pointer flex items-center justify-center flex-grow ${accentStyles[theme]}`}
            >
              <span className="text-lg mr-2">📷</span>
              {formData.image ? "Change image" : "Upload image"}
            </label>
            {formData.image && (
              <button
                type="button"
                onClick={() => setFormData({ ...formData, image: null })}
                className="text-red-500 p-2"
              >
                ✕
              </button>
            )}
          </div>
          {formData.image && (
            <div className="mt-2">
              <img
                src={formData.image}
                alt="Purchase preview"
                className="w-full h-48 object-cover rounded-xl"
              />
            </div>
          )}
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">How often will you use it?</label>
          <select
            name="usageFrequency"
            value={formData.usageFrequency}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-opacity-50 focus:ring-blue-500"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="occasionally">Occasionally</option>
            <option value="rarely">Rarely</option>
            <option value="never">Probably never</option>
          </select>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Still happy you bought it?</label>
          <div className="flex gap-2">
            {["yes", "kinda", "no"].map(value => (
              <button
                type="button"
                key={value}
                onClick={() => setFormData({ ...formData, stillHappy: value })}
                className={`flex-1 px-4 py-3 rounded-xl border ${
                  formData.stillHappy === value
                    ? `${accentStyles[theme]} border-2`
                    : "border-gray-300"
                }`}
              >
                {value.charAt(0).toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm text-gray-600 mb-1">Would you buy it again?</label>
          <div className="flex gap-2">
            {["hellyes", "maybe", "nope"].map(value => (
              <button
                type="button"
                key={value}
                onClick={() => setFormData({ ...formData, buyAgain: value })}
                className={`flex-1 px-4 py-3 rounded-xl border ${
                  formData.buyAgain === value
                    ? `${accentStyles[theme]} border-2`
                    : "border-gray-300"
                }`}
              >
                {value === "hellyes" ? "Hell yes" : value.charAt(0).toUpperCase() + value.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <motion.button
          type="submit"
          className={`w-full py-4 rounded-xl text-white font-bold text-lg ${buttonStyles[theme]}`}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Calculate My Guilt Score
        </motion.button>
      </form>
    </motion.div>
  );
};

export default PurchaseInput;
